/**
 * O GRUPO ECONÔMICO NA LISTA (Assinaturas; Faturas vem depois).
 * Prévia: https://claude.ai/artifact/6Eovd5WDr25fRREhY6T5jW
 *
 * Existem TRÊS coisas chamadas grupo, e a lista só conhecia duas:
 *   1. CLIENTE com vários contratos   → o "Agrupado" de sempre (por clientId)
 *   2. LOCAIS dentro de um contrato   → o selo "+N filiais" (client_locations)
 *   3. GRUPO ECONÔMICO                → empresas diferentes, parentClientId
 * O "+1 filiais" da CONCEITO BRASIL é do tipo 2, um local da própria matriz, e
 * não tem relação com a PRÉ-FABRICADOS, que é outra empresa. Por isso as duas
 * ficavam lado a lado mesmo agrupado.
 *
 * Aqui entra o tipo 3 como nível ACIMA do cliente. Quem pertence a qual grupo
 * vem do rateio (`montarPainelDoGrupo`, o mesmo da ficha e de Grupos
 * Econômicos): três telas, um cálculo. Grupo sem rateio não vira linha de grupo.
 * Em 10/2026 isso dá 6 das 142 assinaturas ativas; as outras 136 não mudam.
 */

/** Matrizes que valem consultar: o grupo tem 2+ empresas com assinatura na lista. */
export function matrizesCandidatas(
  clients: Array<{ id: number; parentClientId?: number | null }>,
  contracts: Array<{ clientId: number }>,
): number[] {
  const comContrato = new Set(contracts.map((c) => c.clientId));
  const membros = new Map<number, Set<number>>();
  for (const cl of clients) {
    const matriz = cl.parentClientId ?? null;
    if (matriz == null) continue;
    const set = membros.get(matriz) || new Set<number>([matriz]);
    set.add(cl.id);
    membros.set(matriz, set);
  }
  const out: number[] = [];
  membros.forEach((set, matriz) => {
    let n = 0;
    set.forEach((id) => { if (comContrato.has(id)) n++; });
    if (n >= 2) out.push(matriz);
  });
  return out.sort((a, b) => a - b);
}

export type LinhaDeTopo<G> =
  | { tipo: 'cliente'; grupo: G }
  | { tipo: 'grupo'; matrizId: number; membros: G[] };

/**
 * Reorganiza os grupos-por-cliente em linhas de topo. O grupo econômico entra na
 * posição do seu PRIMEIRO membro na ordem que veio (a ordenação da tabela vale),
 * e leva junto os demais. Grupo com só um membro visível (busca, filtro) também
 * vira linha de grupo: a soma é do grupo, e saber que há grupo é informação.
 */
export function linhasDeTopo<G>(grupos: G[], clientIdDe: (g: G) => number, matrizDe: Map<number, number>): LinhaDeTopo<G>[] {
  const out: LinhaDeTopo<G>[] = [];
  const pos = new Map<number, number>();
  for (const g of grupos) {
    const matriz = matrizDe.get(clientIdDe(g));
    if (matriz == null) {
      out.push({ tipo: 'cliente', grupo: g });
      continue;
    }
    const i = pos.get(matriz);
    if (i == null) {
      pos.set(matriz, out.length);
      out.push({ tipo: 'grupo', matrizId: matriz, membros: [g] });
    } else {
      (out[i] as { membros: G[] }).membros.push(g);
    }
  }
  // Dentro do grupo, a matriz primeiro.
  for (const l of out) {
    if (l.tipo === 'grupo') l.membros.sort((a, b) => Number(clientIdDe(b) === l.matrizId) - Number(clientIdDe(a) === l.matrizId));
  }
  return out;
}

/**
 * A BUSCA QUE CASA UM MEMBRO TRAZ O GRUPO INTEIRO (09/10/2026, Assinaturas no Agrupado). A busca filtra por
 * assinatura, antes de agrupar: digitar "FISCAL" deixava passar só a assinatura da FISCALMAX, e o grupo da CONTROLSUL
 * aparecia com o cabeçalho e uma linha, como se o grupo fosse só ela. Agora a assinatura que casou por IDENTIDADE
 * (nome, CNPJ, código) traz todas as do grupo econômico dela. A que casou só pelas observações não puxa o grupo: ela vai
 * para o bloco próprio, e uma observação que cita outro cliente não pode abrir o grupo de alguém.
 *
 * Devolve as assinaturas filtradas, acrescidas das do grupo, e as matrizes que a busca abriu.
 */
export function comOGrupoInteiro<C extends { id: number; clientId: number }>(
  filtradas: C[],
  todas: C[],
  matrizDe: Map<number, number>,
  casouPorIdentidade: (c: C) => boolean,
): { assinaturas: C[]; matrizesAbertas: Set<number> } {
  const matrizesAbertas = new Set<number>();
  for (const c of filtradas) {
    const m = matrizDe.get(c.clientId);
    if (m != null && casouPorIdentidade(c)) matrizesAbertas.add(m);
  }
  if (!matrizesAbertas.size) return { assinaturas: filtradas, matrizesAbertas };
  const ja = new Set(filtradas.map((c) => c.id));
  const doGrupo = todas.filter((c) => !ja.has(c.id) && matrizesAbertas.has(matrizDe.get(c.clientId) as number));
  return { assinaturas: [...filtradas, ...doGrupo], matrizesAbertas };
}

// ─── Faturas ────────────────────────────────────────────────────────────────

export interface FaturaDoGrupo {
  clientId: number;
  totalValue: number | string;
  dueDate?: string | null;
  status?: string;
  nossoNumero?: string | null;
  boletoStatus?: string | null;
}

export interface ResumoDoGrupoNaFatura {
  total: number;
  /** "10/10/2026" ou "25 a 26/10/2026": a divergência de vencimento é informação. */
  vencimento: string;
  vencimentosDivergem: boolean;
  /** Empresas do grupo com fatura nesta competência / empresas do grupo. */
  faturadas: number;
  empresas: number;
  boletosConfirmados: number;
  comBoleto: number;
}

const ddmmaaaa = (d: string) => d.split('-').reverse().join('/');

/**
 * A linha do grupo na lista de faturas responde, sem abrir nada: faturou todo o
 * grupo (2 de 2, 4 de 4)? os boletos estão confirmados? (Os e-mails vêm do
 * mapa de envio da tela.) ⛔ É LEITURA: não existe emitir, baixar boleto nem
 * enviar "do grupo". Cada empresa tem fatura, nota e boleto próprios por decisão
 * de 10/2026.
 */
export function resumoDoGrupoNaFatura(faturas: FaturaDoGrupo[], empresasDoGrupo: number[]): ResumoDoGrupoNaFatura {
  const total = Math.round(faturas.reduce((s, f) => s + (Number(f.totalValue) || 0), 0) * 100) / 100;
  const datas = Array.from(new Set(faturas.map((f) => String(f.dueDate || '').split('T')[0]).filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d)))).sort();
  let vencimento = '—';
  if (datas.length === 1) vencimento = ddmmaaaa(datas[0]);
  else if (datas.length > 1) {
    const [a, b] = [datas[0], datas[datas.length - 1]];
    vencimento = a.slice(0, 7) === b.slice(0, 7) ? `${a.slice(8, 10)} a ${ddmmaaaa(b).slice(0, 5)}` : `${ddmmaaaa(a).slice(0, 5)} a ${ddmmaaaa(b).slice(0, 5)}`;
  }
  const clientes = new Set(faturas.filter((f) => f.status !== 'Cancelada').map((f) => f.clientId));
  const comBoleto = faturas.filter((f) => f.nossoNumero).length;
  const boletosConfirmados = faturas.filter((f) => f.nossoNumero && ['confirmado', 'pago'].includes(String(f.boletoStatus || '').toLowerCase())).length;
  return {
    total,
    vencimento,
    vencimentosDivergem: datas.length > 1,
    faturadas: empresasDoGrupo.filter((id) => clientes.has(id)).length,
    empresas: empresasDoGrupo.length,
    boletosConfirmados,
    comBoleto,
  };
}

// ─── Grupo de um só (decisão G1 do Heitor, 03/11/2026) ──────────────────────
// Prévia: https://claude.ai/artifact/7ekR55Fd6UFamJbzFzt7Ah

/**
 * A MOLDURA DE GRUPO só existe com 2 ou mais empresas no RATEIO VIGENTE (G1).
 *
 * O caso: CONTROLSUL. A versão 7 do rateio tem uma linha só (CONTROLSUL, 100%); a FISCALMAX saiu e a assinatura dela
 * está Suspensa. A lista mostrava "grupo · 1" em Assinaturas e "grupo · 1 fatura" em Faturas, uma moldura que soma uma
 * empresa só. Abaixo de 2, a linha é a de sempre, com a marca de `marcaDoGrupoSolto`.
 *
 * ⚠️ É a regra do GRUPO, não do que está visível: busca e filtro continuam sem desmontar grupo de verdade
 * (`linhasDeTopo`, `comOGrupoInteiro`). Por isso ela olha o painel, e não a contagem de linhas.
 */
export function temMolduraDeGrupo(painel: { empresas: unknown[] } | null | undefined): boolean {
  return !!painel && painel.empresas.length >= 2;
}

export interface MarcaDoGrupoSolto {
  matrizId: number;
  nome: string;
  /** Empresas do grupo (a matriz e as que apontam para ela) com assinatura Ativa. */
  ativas: number;
  /** Empresas do grupo pela estrutura societária (`parentClientId`). */
  empresas: number;
}

/**
 * A marca na linha solta: "grupo CONTROLSUL, 1 de 2 empresas ativas". Saber que há grupo continua sendo informação,
 * mesmo quando ele não divide nada.
 */
export function marcaDoGrupoSolto(
  matrizId: number,
  nome: string,
  clients: Array<{ id: number; parentClientId?: number | null }>,
  contracts: Array<{ clientId: number; status?: string }>,
): MarcaDoGrupoSolto {
  const ids = new Set<number>([matrizId]);
  for (const c of clients) if (c.parentClientId === matrizId) ids.add(c.id);
  const comAtiva = new Set(contracts.filter((c) => c.status === 'Ativo').map((c) => c.clientId));
  let ativas = 0;
  ids.forEach((id) => { if (comAtiva.has(id)) ativas++; });
  return { matrizId, nome, ativas, empresas: ids.size };
}

export const textoDaMarcaDoGrupo = (m: MarcaDoGrupoSolto) =>
  `grupo ${m.nome}, ${m.ativas} de ${m.empresas} ${m.empresas === 1 ? 'empresa ativa' : 'empresas ativas'}`;
